import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ChatSession, ChatMessage, CurrentUser } from '../types/api';
import { chatApi, userApi } from '../services/api';

interface ChatState {
  // Sessions
  sessions: ChatSession[];
  currentSession: ChatSession | null;
  isLoadingSessions: boolean;
  
  // Messages
  messages: ChatMessage[];
  isLoadingMessages: boolean;
  isSending: boolean;
  
  // User
  currentUser: CurrentUser | null;
  
  // Actions
  loadSessions: () => Promise<void>;
  selectSession: (sessionId: string) => Promise<void>;
  createSession: (title?: string) => Promise<ChatSession>;
  sendMessage: (content: string, imageUrl?: string) => Promise<void>;
  archiveSession: (sessionId: string) => Promise<void>;
  loadProfile: () => Promise<void>;
  setCurrentUser: (user: CurrentUser | null) => void;
  clearChat: () => void;
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      // Initial state
      sessions: [],
      currentSession: null,
      isLoadingSessions: false,
      messages: [],
      isLoadingMessages: false,
      isSending: false,
      currentUser: null,

      // Load all sessions
      loadSessions: async () => {
        set({ isLoadingSessions: true });
        try {
          const sessions = await chatApi.getSessions();
          set({ sessions, isLoadingSessions: false });
          
          // Auto-select first session if none selected
          if (sessions.length > 0 && !get().currentSession) {
            await get().selectSession(sessions[0].id);
            
            // Limit to last 10 messages
            const allMessages = get().messages;
            if (allMessages.length > 10) {
              set({ messages: allMessages.slice(-10) });
            }
          } else if (sessions.length === 0) {
            // No sessions exist, create one automatically
            await get().createSession();
          }
        } catch (error) {
          console.error('Failed to load sessions:', error);
          set({ isLoadingSessions: false });
        }
      },

      // Select and load a session
      selectSession: async (sessionId: string) => {
        const session = get().sessions.find((s) => s.id === sessionId);
        if (!session) return;

        set({ currentSession: session, isLoadingMessages: true, messages: [] });
        try {
          const messages = await chatApi.getMessages(sessionId);
          set({ messages, isLoadingMessages: false });
        } catch (error) {
          console.error('Failed to load messages:', error);
          set({ isLoadingMessages: false });
        }
      },

      // Create a new session
      createSession: async (title?: string) => {
        const session = await chatApi.createSession({ title });
        set((state) => ({
          sessions: [session, ...state.sessions],
          currentSession: session,
          messages: [],
        }));
        return session;
      },

      // Send a message
      sendMessage: async (content: string, imageUrl?: string) => {
        const { currentSession } = get();
        if (!currentSession) {
          // Create a session if none exists
          const session = await get().createSession();
          set({ currentSession: session });
        }

        const sessionId = get().currentSession!.id;
        
        // Optimistically add user message
        const tempUserMessage: ChatMessage = {
          id: `temp-${Date.now()}`,
          sessionId,
          role: 'User',
          content,
          imageUrl,
          createdAt: new Date().toISOString(),
        };
        
        set((state) => ({
          messages: [...state.messages, tempUserMessage],
          isSending: true,
        }));

        try {
          const response = await chatApi.sendMessage(sessionId, { content, imageUrl });
          
          // Replace temp message with real one and add assistant response
          set((state) => ({
            messages: [
              ...state.messages.filter((m) => m.id !== tempUserMessage.id),
              { ...tempUserMessage, id: `user-${Date.now()}` },
              response,
            ],
            isSending: false,
          }));

          // Update session's lastMessageAt
          set((state) => ({
            sessions: state.sessions.map((s) =>
              s.id === sessionId ? { ...s, lastMessageAt: new Date().toISOString() } : s
            ),
          }));
        } catch (error) {
          // Remove optimistic message on error
          set((state) => ({
            messages: state.messages.filter((m) => m.id !== tempUserMessage.id),
            isSending: false,
          }));
          throw error;
        }
      },

      // Archive a session
      archiveSession: async (sessionId: string) => {
        await chatApi.archiveSession(sessionId);
        set((state) => {
          const newSessions = state.sessions.filter((s) => s.id !== sessionId);
          const newCurrentSession =
            state.currentSession?.id === sessionId
              ? newSessions[0] || null
              : state.currentSession;
          return {
            sessions: newSessions,
            currentSession: newCurrentSession,
            messages: newCurrentSession ? state.messages : [],
          };
        });
      },

      // Load user profile
      loadProfile: async () => {
        try {
          const user = await userApi.getProfile();
          set({ currentUser: user });
        } catch (error) {
          console.error('Failed to load profile:', error);
        }
      },

      // Set current user (after linking account)
      setCurrentUser: (user) => set({ currentUser: user }),

      // Clear chat state (on logout)
      clearChat: () =>
        set({
          sessions: [],
          currentSession: null,
          messages: [],
          currentUser: null,
        }),
    }),
    {
      name: 'wise-assist-chat',
      partialize: (state) => ({
        currentUser: state.currentUser,
      }),
    }
  )
);
